import React, { useEffect } from "react";

import { Paper, Grid, Typography, Toolbar, Box } from "@mui/material";
import { Button, ButtonGroup } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChartOptions } from "chart.js";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { OrderByTypeGroup, OrderByTypeObj } from "../../../../models/order.type.dashboard";
import DashboardService, { OrderType } from "../../../../services/dashboard.service";
import { useAppState } from "../../../../context/StateContext";
import { DateFilterTypes } from "../../../../util/enums";
import groupBy from "../../../../util/groupBy";
import { translateMonthLabel } from "../../../../util/translateMonth";
import { Chart as ChartComponent, ChartType } from "../../../Generic/Chart";
import {
  textInfoB,
  errorColorB,
  textWarnC,
  purpleA,
  brandSuccessColor,
  textSuccessB,
  textWarnB,
  textGrayC,
  primaryColor,
} from "../../DashStyleGuides";
import NoDataLabel from "../../NoDataLabel/index";
import buttonStyles from "../button.styles";
import { useStyles } from "./styles";

const bgDashColors = [
  textWarnC,
  purpleA,
  brandSuccessColor,
  textSuccessB,
  textWarnB,
  textGrayC,
  primaryColor,
  errorColorB,
];

const options = {
  maintainAspectRatio: false,
  fill: true,
  pointStyle: "circle",
  pointRadius: 1,
  pointBorderWidth: 1,
  pointBackgroundColor: textInfoB,
  plugins: {
    legend: {
      position: "bottom" as const,
    },
    zoom: {
      zoom: {
        enabled: true,
        mode: "x",
        drag: {
          enabled: true,
        },
        wheel: {
          enabled: true,
        },
      },
    },
  },
};

/** Default Functional Component */
export default function OrdersByType({ isTodaySelected }: { isTodaySelected: boolean }) {
  const classes = useStyles();
  const buttonClass = buttonStyles();
  const { t } = useTranslation();
  const { currentUser } = useAppState();
  const [selIndex, setSelectedIndex] = React.useState<number>(1);
  const [dashDataSet, setDashDataSet] = React.useState<OrderByTypeObj[] | undefined>(undefined);
  const [dashLabelSet, setDashLabelSet] = React.useState<string[] | undefined>(undefined);

  useEffect(() => {
    if (isTodaySelected) {
      setSelectedIndex(isTodaySelected ? 0 : 1);
    }
  }, [isTodaySelected]);

  useQuery(
    ["dashboard.order.by.type", selIndex],
    () =>
      DashboardService.gerOrderByType({
        dateFilterType: Object.values(DateFilterTypes)[selIndex],
        companyId: currentUser?.companyId,
      }),
    {
      onSuccess: (response) => {
        if (response && response?.data) {
          let labels: string[] = [];
          let labelsGroup: { [x: string]: OrderByTypeGroup[] } = {};
          let valuesGroup: { [x: string]: OrderByTypeGroup[] } = {};

          // eslint-disable-next-line default-case
          switch (selIndex) {
            case 0:
              labelsGroup = groupBy(response.data, "hour");
              valuesGroup = groupBy(response.data, "type");
              break;
            case 1:
              labels = funcMonthLabels(response.data);
              valuesGroup = groupBy(response.data, "type");
              break;
            case 2:
              labelsGroup = groupBy(response.data, "year");
              valuesGroup = groupBy(response.data, "type");
              break;
          }
          if (selIndex !== 1)
            Object.keys(labelsGroup).forEach((group) =>
              labels.push(funcLabelConstructor(selIndex, labelsGroup[group]))
            );
          setDashLabelSet(labels);
          const dataSetVals: OrderByTypeObj[] = [];

          Object.keys(valuesGroup).forEach((key, index) => {
            const loadDataSetsArr: OrderByTypeGroup[] = [];

            labels.map((label) => {
              const labelItems = selIndex === 1 ? label.split(" ") : label;
              // eslint-disable-next-line array-callback-return
              const checkIfDateExist = valuesGroup[key].find((value) => {
                if (selIndex === 0)
                  return value.hour === Number(labelItems.toString().split(" ")[0]);
                if (selIndex === 1)
                  return value.month === labelItems[0] && value.year === Number(labelItems[1]);
                if (selIndex === 2) return value.year === Number(labelItems.toString());
              });

              if (checkIfDateExist === undefined) {
                const baseData: { type: string; count: number } = {
                  type: valuesGroup[key][0].type,
                  count: 0,
                };
                let dataInfo: {
                  hour?: number;
                  month?: string;
                  year?: number;
                } = {};

                if (selIndex === 0) dataInfo = { hour: Number(labelItems.toString()) };
                if (selIndex === 1)
                  dataInfo = {
                    month: selIndex === 1 ? labelItems[0].toString() : labelItems.toString(),
                    year: Number(labelItems[1].toString()),
                  };
                if (selIndex === 2) dataInfo = { year: Number(labelItems.toString()) };

                loadDataSetsArr.push({
                  ...baseData,
                  ...dataInfo,
                });
              } else {
                loadDataSetsArr.push(checkIfDateExist);
              }

              return loadDataSetsArr;
            });

            if (index > 0) {
              const addLabelInfo: string = funcLabelConstructor(selIndex, valuesGroup[key]);

              const labelCheck = labels.find((labelItemTitle) => {
                return labelItemTitle === addLabelInfo;
              });

              loadDataSetsArr.push(valuesGroup[key][0]);

              if (labelCheck === undefined) {
                const mutLabelDataSet: string[] = labels;

                mutLabelDataSet.push(addLabelInfo);

                setDashLabelSet(mutLabelDataSet);
              }
            }
            if (loadDataSetsArr !== undefined && loadDataSetsArr[0].type.length > 0) {
              const typeTitle: string = loadDataSetsArr[0].type;
              const typeDataArr: number[] = [];

              loadDataSetsArr.forEach((setItem) => {
                typeDataArr.push(setItem.count);
              });

              dataSetVals.push({
                label: typeTitle,
                data: typeDataArr,
                borderColor: textInfoB,
                backgroundColor: bgDashColors[dataSetVals.length],
                fill: true,
              });
            }
          });

          setDashDataSet(dataSetVals);
        }
      },
      enabled: !!currentUser?.companyId,
    }
  );

  const funcHourLabelLoader = (hour: number) => {
    const convertedNum = hour.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    // eslint-disable-next-line no-nested-ternary
    return Number(convertedNum) < 9
      ? Number(convertedNum) === 0
        ? "12 AM"
        : `${convertedNum} AM`
      : `${convertedNum} PM`;
  };

  const funcLabelConstructor = (dateTypeIndex: number, item: OrderByTypeGroup[]) => {
    if (dateTypeIndex === 0) {
      return funcHourLabelLoader(Number(item[0].hour));
    }
    if (dateTypeIndex === 1) return `${item[0].month} ${item[0].year}`;

    return `${item[0].year}`;
  };

  const funcMonthLabels = (array: OrderType[]) => {
    const labelsArray: string[] = [];

    array.forEach((order) => {
      const labelName = `${order.month} ${order.year}`;
      const find = labelsArray.find((label) => label === labelName);

      if (find === undefined) labelsArray.push(labelName);
    });

    return labelsArray;
  };

  return (
    <>
      <Grid item xs={12}>
        <Paper className={classes.root} elevation={0}>
          <Toolbar className={classes.tblRoot} variant="dense">
            <Typography className={classes.title} color="textSecondary" data-cy="ordersByType">
              {t("ordersByType")}
            </Typography>
            <Box className={classes.bxFlexGrow} />
            <ButtonGroup color="info">
              {Object.keys(DateFilterTypes).map((filter, index) => (
                <Button
                  key={index}
                  className={classnames({
                    [buttonClass.buttonContained]: selIndex === index,
                    [buttonClass.buttonOutlined]: selIndex !== index,
                  })}
                  variant={selIndex === index ? "contained" : "outlined"}
                  onClick={(event) => {
                    event.preventDefault();
                    setSelectedIndex(index);
                  }}
                >
                  {t(filter.toLowerCase())}
                </Button>
              ))}
            </ButtonGroup>
          </Toolbar>
          {dashDataSet !== undefined && dashDataSet.length === 0 ? (
            <NoDataLabel />
          ) : (
            <div className={classnames("chart-container", classes.orderWrapStyles)}>
              {dashLabelSet !== undefined && dashDataSet !== undefined && (
                <ChartComponent
                  id="chartBarId"
                  chartType={ChartType.BAR}
                  data={{ labels: dashLabelSet?.map((l) => translateMonthLabel(l, t)), datasets: dashDataSet }}
                  graphOptions={options as ChartOptions<"bar">}
                />
              )}
            </div>
          )}
        </Paper>
      </Grid>
    </>
  );
}
